/**
 * ReservationHeader Feature Component
 * Displays current reservation details with pickup/dropoff locations and dates
 */

'use client'

import { DateInfo } from '@/components/molecules/DateInfo'
import { DaysBadge } from '@/components/molecules/DaysBadge'
import { LocationInfo } from '@/components/molecules/LocationInfo'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { MapPin } from 'lucide-react'

interface ReservationHeaderProps {
	pickupLocation: string
	dropoffLocation: string
	pickupDate: string
	dropoffDate: string
	days: number
	onModify?: () => void
	className?: string
}

export function ReservationHeader({
	pickupLocation,
	dropoffLocation,
	pickupDate,
	dropoffDate,
	days,
	onModify,
	className,
}: ReservationHeaderProps) {
	return (
		<div
			className={cn(
				'flex flex-col gap-4 py-6 px-[3%] items-center bg-white border-b border-gray-200',
				className
			)}
		>
			{/* Title centered */}
			<h1 className='text-center font-["Bricolage_Grotesque"] text-2xl md:text-3xl font-black text-[#09194D]'>
				RESERVA ACTUAL
			</h1>

			{/* Details row */}
			<div className='w-full flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6'>
				<div className='w-full md:w-auto flex items-center gap-4 md:gap-6 overflow-x-auto md:overflow-visible pb-2 md:pb-0 px-4 md:px-0 scrollbar-hide justify-start md:justify-center'>
					{/* Locations group */}
					<div className='flex gap-2 items-center shrink-0'>
						<MapPin className='w-5 h-5 text-[#09194D]' />
						<div className='flex flex-col gap-2'>
							<LocationInfo
								type='pickup'
								location={pickupLocation}
								compact
							/>
							<LocationInfo
								type='dropoff'
								location={dropoffLocation}
								compact
							/>
						</div>
					</div>

					<Separator
						orientation='vertical'
						className='h-12 bg-[#09194D]/30 shrink-0'
					/>

					{/* Dates group */}
					<div className='shrink-0'>
						<DateInfo
							pickupDate={pickupDate}
							dropoffDate={dropoffDate}
						/>
					</div>

					<Separator
						orientation='vertical'
						className='h-12 bg-[#09194D]/30 shrink-0'
					/>

					{/* Days group */}
					<div className='shrink-0'>
						<DaysBadge
							days={days}
							variant='plain'
							className='items-start'
						/>
					</div>
				</div>

				{/* Modify Button */}
				<Button
					onClick={onModify}
					className='w-full md:w-auto md:ml-2 shrink-0 bg-[#09194D] hover:bg-[#09194D]/90 text-[#CDFF00] font-["Bricolage_Grotesque"] font-bold text-sm px-6 py-2 h-auto rounded-full shadow cursor-pointer'
				>
					Modificar Reserva
				</Button>
			</div>
		</div>
	)
}
